<template>
  <div class="order-item">
    <div class="order-item__main">
      <div class="order-item__image">
        <img :src="getImageLink" alt="" />
      </div>

      <div class="order-item__content-info">
        <div class="order-item__title">{{ orderItem.title }}</div>
        <div v-if="itemTypeLabel" class="order-item__type">
          {{ itemTypeLabel }}
        </div>
      </div>

      <div class="order-item__price">
        {{ formatPriceAmount(orderItem?.total?.amount, locale) }}
      </div>
    </div>

    <div class="order-item__footer">
      <div
        class="order-item__status"
        :class="`order-item__status--${statusVariant}`"
      >
        <component :is="statusIcon" class="order-item__status-icon" />
        <span>{{ statusLabel }}</span>
      </div>

      <div v-if="isItemVisible" class="order-item__links">
        <button
          v-if="orderItem.status === 'CONCLUDED'"
          type="button"
          class="order-item__link"
          @click="handleDownloadFile"
        >
          {{ t('account.orders.downloadPdf') }}
        </button>
        <button
          v-if="orderItem.status === 'CONCLUDED' && isOrderAfterInvoiceCutoff"
          type="button"
          class="order-item__link"
          @click="handleInvoiceAction"
        >
          {{ t('account.orders.generateInvoice') }}
        </button>
        <nuxt-link
          v-if="orderItem?.review_url"
          :to="orderItem?.review_url"
          external
          target="_blank"
          class="order-item__link"
        >
          {{ t('account.orders.review') }}
        </nuxt-link>
      </div>
    </div>
  </div>

  <InvoiceInformationSidebar
    :is-open="isInvoiceSidebarOpen"
    :order-id="orderId"
    :order-item-id="orderItem.id"
    @close="closeInvoiceSidebar"
    @invoice-success="handleInvoiceSuccess"
  />
</template>

<script setup lang="ts">
import { toast, type ToastOptions } from 'vue3-toastify'
import InvoiceInformationSidebar from './InvoiceInformationSidebar.vue'
import { formatPriceAmount } from '~/utils/currencyHelpers'
import IconStatusConfirmed from '~/assets/icons/patch-check.svg?component'
import IconStatusCanceled from '~/assets/icons/dash-circle.svg?component'
import IconStatusProcessing from '~/assets/icons/info-circle.svg?component'

const { t, locale } = useI18n()

const { getAccessToken } = useAuthTokens()

const { orderItem, orderId, hasCustomerInvoice, orderCreatedAt } = defineProps<{
  orderItem: any
  orderId: string
  hasCustomerInvoice?: boolean
  orderCreatedAt: string
}>()

const emit = defineEmits<{
  'invoice-success': [data?: { has_customer_invoice: boolean }]
}>()

const handleInvoiceSuccess = (data?: { has_customer_invoice: boolean }) => {
  // Update the order item's has_customer_invoice status if provided
  if (data?.has_customer_invoice) {
    // We need to emit this up to the parent to update the order
    emit('invoice-success', data)
  } else {
    emit('invoice-success')
  }
}

const isInvoiceSidebarOpen = ref(false)

const openInvoiceSidebar = () => {
  isInvoiceSidebarOpen.value = true
}

const closeInvoiceSidebar = () => {
  isInvoiceSidebarOpen.value = false
}

const isItemVisible = computed(() => {
  if (orderItem.type === 'ADD_ON') return false

  return orderItem.status !== 'CANCELED' || orderItem.review_url
})

const isOrderAfterInvoiceCutoff = computed(() => {
  // This is when we started creating invoices ourselves
  // We can't provide invoices for orders before this date
  const cutoffDate = new Date('2023-09-01')
  const orderDate = new Date(orderCreatedAt)
  return orderDate >= cutoffDate
})

const getImageLink = computed(() => {
  const id = orderItem.media_id

  if (!id) return '/_nuxt3-static/promo-cards/giftbox.webp'
  return `https://media.gokonfetti.com/${id}/200x0/${id}_order.webp`
})

const itemTypeLabel = computed(() => {
  const typeKeys: Record<string, string> = {
    EVENT: 'event',
    PRIVATE_EVENT: 'private',
    GIFT_CARD: 'giftCard',
    ADD_ON: 'addon',
    KIT: 'kit',
  }

  const key = typeKeys[orderItem.type]
  return key ? t(`account.orders.type.${key}`) : null
})

const statusVariant = computed(() => {
  switch (orderItem.status) {
    case 'CONCLUDED':
    case 'DELIVERED':
    case 'PROCESSED':
      return 'concluded'
    case 'CANCELED':
    case 'REJECTED':
      return 'canceled'
    case 'PAYMENT_ERROR':
      return 'error'
    default:
      return 'processing'
  }
})

const statusLabel = computed(() => {
  if (statusVariant.value === 'canceled') {
    return t('account.orders.canceled')
  }

  return t(`account.orders.itemStatus.${statusVariant.value}`)
})

const statusIcon = computed(() => {
  if (statusVariant.value === 'concluded') return IconStatusConfirmed
  if (statusVariant.value === 'processing') return IconStatusProcessing
  return IconStatusCanceled
})

const handleInvoiceAction = () => {
  if (hasCustomerInvoice) {
    downloadInvoice()
  } else {
    openInvoiceSidebar()
  }
}

const downloadInvoice = async () => {
  try {
    const token = await getAccessToken()
    const config = useRuntimeConfig()
    const { apiBaseURL } = config.public

    const response = await fetch(
      `${apiBaseURL}/v1/checkout/orders/${orderId}/download-file`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'INVOICE',
          order_item_id: orderItem.id,
        }),
      }
    )

    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `invoice-${orderItem.id}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  } catch (error) {
    toast(t('general.error'), {
      autoClose: 2000,
      type: 'error',
      position: toast.POSITION.TOP_CENTER,
      multiple: false,
    } as ToastOptions)
  }
}

const handleDownloadFile = async () => {
  const fetchLink = `/v1/checkout/orders/${orderId}/items/${orderItem.id}/files`

  try {
    const token = await getAccessToken()
    const response = await kftApiRequest(fetchLink, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (response && Array.isArray(response) && response.length) {
      window.open(response[0], '_blank')
    }
  } catch (error) {
    toast(t('general.error'), {
      autoClose: 2000,
      type: 'error',
      position: toast.POSITION.TOP_CENTER,
      multiple: false,
    } as ToastOptions)
  }
}
</script>

<style lang="postcss" scoped>
.order-item {
  @apply flex flex-col gap-3 p-4;

  &__main {
    @apply flex flex-row gap-3 items-start;
  }

  &__image {
    @apply relative min-w-[60px] w-[60px] h-[60px] rounded-lg overflow-hidden;

    img {
      @apply w-full h-full object-cover;
    }
  }

  &__content-info {
    @apply flex-1 min-w-0;
  }

  &__title {
    @apply font-medium text-sm;
  }

  &__type {
    @apply text-xs text-gray-500 mt-0.5;
  }

  &__price {
    @apply font-medium text-sm text-gray-900 whitespace-nowrap;
  }

  &__footer {
    @apply flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2;
  }

  &__status {
    @apply flex items-center gap-1.5 text-xs font-medium;

    :deep(svg) {
      @apply w-4 h-4 min-w-4;
    }

    &--concluded {
      @apply text-success;

      :deep(path) {
        fill: currentColor;
      }
    }

    &--processing {
      @apply text-gray-500;

      :deep(path) {
        fill: currentColor;
      }
    }

    &--canceled,
    &--error {
      @apply text-danger;

      :deep(path) {
        fill: currentColor;
      }
    }
  }

  &__links {
    @apply flex items-center gap-2 text-xs;
  }

  &__link {
    @apply text-teal-600 font-medium cursor-pointer;

    &:hover {
      @apply underline;
    }

    &:not(:first-child) {
      @apply pl-2 border-l border-secondary-active;
    }
  }
}
</style>
